<template>
  <v-card
    class="downtime-card"
    elevation="1"
    rounded="lg"
    :loading="loading"
  >
    <!-- Header: title left, KPI right (tighter vertical rhythm) -->
    <div class="card-header">
      <div class="header-left">
        <div class="chart-title">
          Downtime Breakdown
        </div>
        <div class="chart-subtitle">
          {{ subtitle }}
        </div>
      </div>

      <div
        class="kpi"
        :class="severityClass"
      >
        <div class="kpi-value">
          {{ totalLoss }}<span class="kpi-unit">%</span>
        </div>
        <div class="kpi-label">
          Total loss
        </div>
      </div>
    </div>

    <!-- Composition strip: at-a-glance share of each cause -->
    <div
      class="composition-strip"
      role="img"
      aria-label="Downtime composition"
    >
      <div
        v-for="item in sortedBreakdown"
        :key="item.label"
        class="strip-segment"
        :style="{ width: item.pct + '%', background: item.color }"
        :title="`${item.label}: ${item.pct}%`"
      />
    </div>

    <!-- Chart -->
    <apexchart
      v-if="hasData"
      type="bar"
      :height="chartHeight"
      :options="chartOptions"
      :series="series"
    />

    <!-- Empty state -->
    <div
      v-else
      class="empty-state"
    >
      <v-icon
        size="28"
        color="grey lighten-1"
      >
        mdi-chart-timeline-variant
      </v-icon>
      <div class="empty-text">
        No downtime recorded for this period
      </div>
    </div>
  </v-card>
</template>

<script>
import VueApexCharts from "vue-apexcharts";

/** Fixed color per API category key so colors stay consistent across loads */
const CATEGORY_COLORS = {
  hardware_faults: "#14532d",
  telco_comms: "#0d9488",
  maintenance: "#5eead4",
  unknown: "#cbd5e1",
};

export default {
  name: "DowntimeBreakdown",

  components: {
    apexchart: VueApexCharts,
  },

  props: {
    loading: {
      type: Boolean,
      default() {
        return false;
      },
    },

    /**
     * The `downtime_breakdown` object from the API:
     * {
     *   total_downtime_seconds,
     *   total_availability_loss_percent,
     *   categories: { hardware_faults: { label, seconds, share_percent, ... }, ... }
     * }
     */
    items: {
      type: Object,
      default() {
        return {};
      },
    },

    subtitle: {
      type: String,
      default: "Availability loss by root cause",
    },

    /** Above this %, the KPI renders in warning/critical tones */
    warningThreshold: {
      type: Number,
      default: 1,
    },

    criticalThreshold: {
      type: Number,
      default: 3,
    },
  },

  computed: {
    /** categories object -> [{ key, label, pct, seconds, color }] */
    breakdown() {
      const categories = this.items?.categories || {};

      return Object.entries(categories).map(([key, cat]) => ({
        key,
        label: cat.label || key,
        pct: this.round(cat.availability_loss_percent),
        seconds: cat.seconds || 0,
        color: CATEGORY_COLORS[key] || "#94a3b8",
      }));
    },

    totalLoss() {
      return this.round(this.items?.total_availability_loss_percent);
    },

    hasData() {
      return this.breakdown.some((i) => i.pct > 0);
    },

    /** Largest contributor first — reads as a ranked list */
    sortedBreakdown() {
      return [...this.breakdown].sort((a, b) => b.pct - a.pct);
    },

    /** Scale rows with data so 2 or 8 categories both look right */
    chartHeight() {
      return this.sortedBreakdown.length * 44 + 24;
    },

    severityClass() {
      if (this.totalLoss >= this.criticalThreshold) return "kpi--critical";
      if (this.totalLoss >= this.warningThreshold) return "kpi--warning";
      return "kpi--ok";
    },

    series() {
      return [
        {
          name: "Share of downtime",
          data: this.sortedBreakdown.map((i) => i.pct),
        },
      ];
    },

    chartOptions() {
      const maxPct = Math.max(
        ...this.sortedBreakdown.map((i) => i.pct),
        1 // guard against -Infinity / 0 when all values are 0
      );
      const sorted = this.sortedBreakdown;

      return {
        chart: {
          toolbar: { show: false },
          fontFamily: "inherit",
          parentHeightOffset: 0,
          animations: {
            speed: 500,
            animateGradually: { enabled: true, delay: 80 },
          },
        },

        colors: sorted.map((i) => i.color),

        plotOptions: {
          bar: {
            horizontal: true,
            distributed: true,
            borderRadius: 4,
            borderRadiusApplication: "end",
            barHeight: "60%",
          },
        },

        dataLabels: {
          enabled: true,
          textAnchor: "start",
          offsetX: 6,
          formatter: (val) => `${val}%`,
          style: {
            colors: ["#334155"],
            fontSize: "11px",
            fontWeight: 600,
          },
          offsetY: 0,
          dropShadow: { enabled: false },
        },

        legend: { show: false },

        xaxis: {
          categories: sorted.map((i) => i.label),
          // Headroom so end-of-bar labels never clip
          max: Math.ceil(maxPct * 1.2),
          axisTicks: { show: false },
          axisBorder: { show: false },
          labels: { show: false },
        },

        yaxis: {
          labels: {
            style: {
              fontSize: "12px",
              fontWeight: 500,
              colors: "#475569",
            },
            maxWidth: 120,
          },
        },

        grid: {
          show: false,
          padding: { top: -12, bottom: 0, left: 8, right: 16 },
        },

        states: {
          hover: { filter: { type: "darken", value: 0.9 } },
          active: { filter: { type: "none" } },
        },

        tooltip: {
          theme: "dark",
          y: {
            formatter: (val, { dataPointIndex }) => {
              const item = sorted[dataPointIndex];
              const hours = item ? (item.seconds / 3600).toFixed(1) : 0;
              return `${val}% of total downtime (${hours} hrs)`;
            },
            title: { formatter: () => "" },
          },
        },
      };
    },
  },

  methods: {
    round(value) {
      return Math.round((value || 0) * 100) / 100;
    },
  },
};
</script>

<style scoped>
.downtime-card {
  background: #ffffff !important;
  border: 1px solid #e8ecf0 !important;
  padding: 0px 8px 8px 8px;
  transition: box-shadow 0.2s ease;
  height: 100%;
}
.downtime-card:hover {
  box-shadow: 0 4px 16px rgba(15, 42, 42, 0.08) !important;
}

/* ---- Header ---- */
.card-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 18px 20px 12px 20px;
}
.header-left {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
}
.chart-title {
  font-size: 15px;
  font-weight: 600;
  color: #1e293b;
  letter-spacing: -0.01em;
}
.chart-subtitle {
  font-size: 12px;
  color: #94a3b8;
}

/* ---- KPI ---- */
.kpi {
  text-align: right;
  flex-shrink: 0;
}
.kpi-value {
  font-size: 26px;
  font-weight: 700;
  line-height: 1.1;
  font-variant-numeric: tabular-nums;
  letter-spacing: -0.02em;
}
.kpi-unit {
  font-size: 15px;
  font-weight: 600;
  margin-left: 1px;
  opacity: 0.75;
}
.kpi-label {
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #94a3b8;
  margin-top: 2px;
}
.kpi--ok .kpi-value {
  color: #0d9488;
}
.kpi--warning .kpi-value {
  color: #d97706;
}
.kpi--critical .kpi-value {
  color: #dc2626;
}

/* ---- Composition strip ---- */
.composition-strip {
  display: flex;
  height: 6px;
  border-radius: 3px;
  overflow: hidden;
  margin: 0 20px 14px 20px;
  gap: 2px;
}
.strip-segment {
  height: 100%;
  min-width: 4px;
  transition: opacity 0.15s ease;
}
.strip-segment:hover {
  opacity: 0.8;
}

/* ---- Empty state ---- */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 36px 20px 44px;
}
.empty-text {
  font-size: 13px;
  color: #94a3b8;
}
</style>
